import { useState, useEffect, useCallback } from 'react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { ReviewQueueItem } from '../types/review-queue'; // We might need to define this type if not exported

// Defining the type here based on usage in ReviewQueuePanel if we can't find it easily
export interface ReviewQueueItem {
  id: string;
  name: string;
  type: string;
  companyId: string;
  companyName: string;
  category?: string;
  description?: string;
  submittedForReviewAt?: string;
  assignedToName?: string;
  engagementName?: string; // Metadata requested
  dueDate?: string; // Metadata requested
  lastUpdated?: string; // Metadata requested
  status?: string;
  isGlobal?: boolean;
}

interface UseReviewQueueUnifiedProps {
  accessToken: string;
  currentUser: any;
}

interface ReviewQueueUnifiedResult {
  items: ReviewQueueItem[];
  totalCount: number;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  debugInfo: {
    source: string;
    count: number;
  };
}

export function useReviewQueueUnified({ accessToken, currentUser }: UseReviewQueueUnifiedProps): ReviewQueueUnifiedResult {
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [debugInfo, setDebugInfo] = useState({ source: '', count: 0 });

  const loadItems = useCallback(async () => {
    if (!accessToken) return;

    const url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/review-queue`;
    const sourceName = 'consultant/review-queue';

    try {
      setLoading(true);
      const response = await fetch(url, {
        headers: getAuthHeaders(accessToken),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch review queue: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const rawItems = data.items || [];

      setItems(rawItems);
      setDebugInfo({
        source: sourceName,
        count: rawItems.length
      });
      setError(null);

    } catch (err) {
      console.error("Review Queue Unified Load Failure", err);
      setError(err instanceof Error ? err : new Error('Unknown error loading review queue'));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  return {
    items,
    totalCount: items.length,
    loading,
    error,
    refresh: loadItems,
    debugInfo
  };
}